import React, { useState } from 'react';
import moment from 'moment';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import config from '@/configs/config';
import { useFridgeContext } from '@/contexts/useFridgeContext';
import helper, { DELIVERY_METHOD } from '@/helper';

import Alert from '@/components/Alert';
import TextField from '@/components/TextField';

const useStyles = makeStyles(() => ({
  options: {
    '& span:nth-child(2)': {
      width: '100%',
    },
  },
}));

const LOCATION_ALERT = 'LOCATION_ALERT';

const now = new Date().getTime();

const DeliveryMethodInput = ({
  preOrder = {},
  defaultValue = {},
  onChange = () => {},
}) => {
  const classes = useStyles();

  const defaultDeliveryMethod = (defaultValue.fridgeId === DELIVERY_METHOD.DELIVERY)
    ? DELIVERY_METHOD.DELIVERY : DELIVERY_METHOD.FRIDGE;
  const defaultIndicateFridge = (defaultValue.fridgeId !== DELIVERY_METHOD.DELIVERY && defaultValue.fridgeId) || 'default';
  const defaultIndicateBatch = defaultValue.batch || 'default';
  const defaultIndicateDate = defaultValue.date || 'default';
  const defaultIndicateTimeSlot = defaultValue.timeSlot || 'default';

  const { fridgeDictionary } = useFridgeContext();
  const [activeAlert, setActiveAlert] = React.useState(null);
  const [isFridgeAvailable, setFridgeAvailable] = React.useState(true);
  const [isDeliveryAvailable, setDeliveryAvailable] = React.useState(true);

  const [deliveryMethod, setDeliveryMethod] = useState(defaultDeliveryMethod);
  const [indicateFridge, setIndicateFridge] = React.useState(defaultIndicateFridge);
  const [indicateBatch, setIndicateBatch] = React.useState(defaultIndicateBatch);
  const [indicateDate, setIndicateDate] = React.useState(defaultIndicateDate);
  const [indicateTimeSlot, setIndicateTimeSlot] = React.useState(defaultIndicateTimeSlot);

  const handleSelectFridge = React.useCallback((fridgeId) => {
    const fridge = fridgeDictionary[fridgeId];
    if (fridge?.locationPrompt) {
      setActiveAlert(LOCATION_ALERT);
    }
    setIndicateFridge(fridgeId);
  }, [fridgeDictionary]);

  React.useEffect(() => {
    if (
      defaultDeliveryMethod === deliveryMethod
       && defaultIndicateFridge === indicateFridge
       && defaultIndicateBatch === indicateBatch
       && defaultIndicateDate === indicateDate
       && defaultIndicateTimeSlot === indicateTimeSlot
    ) return;
    if (deliveryMethod === DELIVERY_METHOD.DELIVERY) {
      setIndicateFridge(defaultIndicateFridge);
      setIndicateBatch(defaultIndicateBatch);
      onChange({
        fridgeId: DELIVERY_METHOD.DELIVERY,
        date: indicateDate,
        timeSlot: indicateTimeSlot,
        batch: null,
      });
    } else {
      setIndicateDate(defaultIndicateDate);
      setIndicateTimeSlot(defaultIndicateTimeSlot);
      onChange({
        fridgeId: indicateFridge,
        date: null,
        timeSlot: null,
        batch: indicateBatch,
      });
    }
  }, [ // eslint-disable-line 
    defaultDeliveryMethod,
    deliveryMethod,
    defaultIndicateFridge,
    indicateFridge,
    defaultIndicateBatch,
    indicateBatch,
    defaultIndicateDate,
    indicateDate,
    defaultIndicateTimeSlot,
    indicateTimeSlot,
  ]);

  const fridgeOptions = React.useMemo(() => {
    const options = Object.keys(preOrder.fridgeIds || {}).map((fridgeId) => (
      <MenuItem key={fridgeId} value={fridgeId}>
        {preOrder.fridgeIds[fridgeId]}
      </MenuItem>
    )).filter((d) => d);
    if (options.length === 0 && isFridgeAvailable) {
      setFridgeAvailable(false);
    }
    return options;
  }, [preOrder, isFridgeAvailable]);

  const batchOptions = React.useMemo(() => {
    const options = Object.keys(preOrder.batches || {}).filter((key) => (
      (preOrder.batches[key].latestRegisterTime > now)
    )).sort((key1, key2) => (
      (preOrder.batches[key1].latestRegisterTime || 0)
      - (preOrder.batches[key2].latestRegisterTime || 0)
    )).map((key) => (
      <MenuItem key={key} value={key}>
        {moment(preOrder.batches[key].preOrderArrivedNotificationTime).format('YYYY-MM-DD HH:mm')}
        ~
        {moment(preOrder.batches[key].redeemEndTime).format('YYYY-MM-DD HH:mm')}
      </MenuItem>
    ))
      .filter((d) => d);
    if (options.length === 0 && isFridgeAvailable) {
      setFridgeAvailable(false);
    }
    return options;
  }, [preOrder, isFridgeAvailable]);

  const deliveryDateOptions = React.useMemo(() => {
    const today = moment().format('YYYY-MM-DD');
    const options = !preOrder?.deliverySupported ? []
      : Object.values(preOrder?.deliveryAvailableDate || {})
        .filter(({ latestRegisterTime }) => moment(latestRegisterTime).isAfter(moment()))
        .map(({ date }) => (date && date > today ? (
          <MenuItem key={date} value={date}>{date}</MenuItem>
        ) : null)).filter((d) => d);
    if (options.length === 0 && isDeliveryAvailable) {
      setDeliveryAvailable(false);
    }
    return options;
  }, [preOrder, isDeliveryAvailable]);

  const deliveryTimeSlotOptions = React.useMemo(() => {
    const options = !preOrder?.deliverySupported ? []
      : (preOrder.deliveryAvailableTimeSlot || '').split(',').map((timeSlot) => (timeSlot ? (
        <MenuItem key={timeSlot} value={timeSlot}>
          {timeSlot}
        </MenuItem>
      ) : null)).filter((d) => d);
    if (options.length === 0 && isDeliveryAvailable) {
      setDeliveryAvailable(false);
    }
    return options;
  }, [preOrder, isDeliveryAvailable]);

  React.useEffect(() => {
    if (deliveryMethod === DELIVERY_METHOD.FRIDGE && !isFridgeAvailable) {
      if (isDeliveryAvailable) {
        setDeliveryMethod(DELIVERY_METHOD.DELIVERY);
      } else {
        setDeliveryMethod('default');
      }
    }
    if (deliveryMethod === DELIVERY_METHOD.DELIVERY && !isDeliveryAvailable) {
      if (isFridgeAvailable) {
        setDeliveryMethod(DELIVERY_METHOD.FRIDGE);
      } else {
        setDeliveryMethod('default');
      }
    }
  }, [isFridgeAvailable, isDeliveryAvailable, deliveryMethod]);

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <RadioGroup
            className={classes.options}
            width="100%"
            value={deliveryMethod}
            onChange={(event) => { setDeliveryMethod(event.target.value); }}
          >
            <FormControlLabel
              disabled={!isFridgeAvailable}
              value={DELIVERY_METHOD.FRIDGE}
              style={{ alignItems: 'flex-start', textAlign: 'left' }}
              control={<Radio />}
              label={(
                <Box width="100%" mt={1} position="relative">
                  <Box fontWeight="bold">
                    自取｜請選擇您希望取貨的
                    {config.BrandNameTW}
                  </Box>
                  {
                      deliveryMethod === DELIVERY_METHOD.FRIDGE && (
                        <>
                          <Select
                            disabled={!isFridgeAvailable}
                            style={{ position: 'absolute', width: '100%' }}
                            value={indicateFridge}
                            onChange={(event) => handleSelectFridge(event.target.value)}
                            input={<TextField />}
                          >
                            <MenuItem key="default" value="default">
                              請選擇您希望取貨的
                              {config.BrandNameTW}
                            </MenuItem>
                            {fridgeOptions}
                          </Select>
                          <Box height="50px"> </Box>
                          {
                            (indicateFridge && fridgeDictionary[indicateFridge]?.address) && (
                              <Box mb={1}>
                                <Typography variant="caption">{`地址: ${fridgeDictionary[indicateFridge]?.address}`}</Typography>
                              </Box>
                            )
                          }
                          <Box fontWeight="bold">開放自取的日期</Box>
                          <Select
                            disabled={!isFridgeAvailable}
                            style={{ position: 'absolute', width: '100%' }}
                            value={indicateBatch}
                            onChange={(event) => setIndicateBatch(event.target.value)}
                            input={<TextField />}
                          >
                            <MenuItem key="default" value="default">請選擇您希望取貨的日期</MenuItem>
                            {batchOptions}
                          </Select>
                          <Box height="50px"> </Box>
                        </>
                      )
                    }
                </Box>
              )}
            />
            <FormControlLabel
              disabled={!isDeliveryAvailable}
              value={DELIVERY_METHOD.DELIVERY}
              style={{ alignItems: 'flex-start', textAlign: 'left' }}
              control={<Radio />}
              label={(
                <Box width="100%" mt={1} position="relative">
                  <Box fontWeight="bold">
                    {`宅配 運費${helper.getAppropriatePriceByRules(preOrder?.deliveryFeeRules, 1)}元 `}
                    <Typography variant="caption">運費規則：同一廠商同一時段可合併運費</Typography>
                  </Box>
                  {
                    deliveryMethod === DELIVERY_METHOD.DELIVERY && (
                      <>
                        <Box>開放配達的時段</Box>
                        <Grid container position="relative" spacing={1}>
                          <Grid item xs={6} position="relative">
                            <Select
                              disabled={!isDeliveryAvailable}
                              style={{ position: 'absolute', width: '50%' }}
                              value={indicateDate}
                              onChange={(event) => setIndicateDate(event.target.value)}
                              input={<TextField />}
                            >
                              <MenuItem key="default" value="default">請選擇您希望取貨的日期</MenuItem>
                              {deliveryDateOptions}
                            </Select>
                            <Box height="50px"> </Box>
                          </Grid>
                          <Grid item xs={6} position="relative">
                            <Select
                              disabled={!isDeliveryAvailable}
                              style={{ position: 'absolute', width: '50%' }}
                              value={indicateTimeSlot}
                              onChange={(event) => setIndicateTimeSlot(event.target.value)}
                              input={<TextField />}
                            >
                              <MenuItem key="default" value="default">請選擇您希望取貨的時段</MenuItem>
                              {deliveryTimeSlotOptions}
                            </Select>
                            <Box height="50px"> </Box>
                          </Grid>
                        </Grid>
                      </>
                    )
                  }
                </Box>
              )}
            />
          </RadioGroup>
        </Grid>
      </Grid>
      <Alert
        isOpen={activeAlert === LOCATION_ALERT}
        title="地點提示"
        content={fridgeDictionary[indicateFridge]?.locationPrompt}
        buttons={(
          <Button fullWidth onClick={() => setActiveAlert(null)}>
            <Box fontWeight="fontWeightBold" color="support.main">
              我知道了
            </Box>
          </Button>
        )}
      />
    </>
  );
};

export default DeliveryMethodInput;

export const isDeliveryMethodDataComplete = (deliveryMethod, preOrder) => {
  if (!deliveryMethod || !preOrder) return false;
  const {
    fridgeId,
    date,
    timeSlot,
    batch,
  } = deliveryMethod;
  if (fridgeId === DELIVERY_METHOD.DELIVERY) {
    if (!date || !timeSlot || date === 'default' || timeSlot === 'default') return false;
    if (Object.keys(preOrder?.deliveryAvailableDate || {}).indexOf(date) < 0) return false;
    if ((preOrder?.deliveryAvailableTimeSlot || '').indexOf(timeSlot) < 0) return false;
    if (+moment(date) < now) return false;
    return true;
  } if (fridgeId !== 'default') {
    if (!batch || batch === 'default') return false;
    if (!(preOrder?.batches || {})[batch]) return false;
    if ((preOrder?.batches || {})[batch].latestRegisterTime < now) return false;
    if (!preOrder?.fridgeIds[fridgeId]) return false;
    return true;
  }
  return false;
};
